import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { Table, TableColumn, TableAction } from "@/components/Table";
import { PageHeader } from "@/components";
import { Button } from "@/components/Button";
import { z } from "zod";
import { useStoredPreference } from "@/hooks/useStoredPreference";
import { Modal } from "@/components/Modal";
import { Input } from "@/components/Input";
import { Tabs, TabItem } from "@/components/Tabs";
import { Select } from "@/components/Select";
import { Badge } from "@/components/Badge";
import { Icon } from "@/components/Icon";
import { Tooltip } from "@/components/Tooltip";
import { PasswordModal } from "@/pages/Passwords/PasswordModal";
import { PasswordGroupsView } from "@/pages/Passwords/PasswordGroupsView";
import {
    passwordApi,
    passwordGroupApi,
    authApi,
    passwordUserGroupApi,
} from "@/lib/api";
import { Password, PasswordFormData, PasswordGroup } from "@/types/password";
import SearchInput from "@/components/SearchInput";
import { SearchableMultiSelect } from "@/components/SearchableMultiSelect";
import { formatDate } from "@/utils/format";

export function PasswordsList() {
    const { t } = useTranslation("global");
    const { user } = useAuth();
    const { showSuccess, showError } = useToast();
    const { tab, groupId: groupIdParam } = useParams<{
        tab?: string;
        groupId?: string;
    }>();
    const navigate = useNavigate();

    const [items, setItems] = useState<Password[]>([]);
    const [groups, setGroups] = useState<PasswordGroup[]>([]);
    const [users, setUsers] = useState<
        { id: number; name: string; email: string }[]
    >([]);
    const [pugs, setPugs] = useState<{ id: number; name: string }[]>([]);
    const [loading, setLoading] = useState(true);

    const [activeTab, setActiveTab] = useState<"list" | "table" | "groups">(
        "list"
    );
    const [groupsSubTab, setGroupsSubTab] = useState<
        "password_groups" | "user_groups"
    >("password_groups");
    const [triggerCreateUserGroup, setTriggerCreateUserGroup] =
        useState<number>(0);
    const [selectedGroupId, setSelectedGroupId] = useStoredPreference<
        number | "all" | "null"
    >(
        "vigepra_password_group",
        z.union([z.number(), z.literal("all"), z.literal("null")]),
        "all",
        true
    );
    const [searchQuery, setSearchQuery] = useStoredPreference(
        "vigepra_password_search",
        z.string().max(500),
        "",
        true
    );
    const [openMenuId, setOpenMenuId] = useState<number | null>(null);
    const [copiedState, setCopiedState] = useState<{
        id: number;
        field: "username" | "password";
    } | null>(null);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPassword, setSelectedPassword] = useState<Password | null>(
        null
    );

    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [selectedPasswordToView, setSelectedPasswordToView] =
        useState<Password | null>(null);

    const openDetails = (password: Password) => {
        setSelectedPasswordToView(password);
        setIsDetailsModalOpen(true);
    };

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [passwordToDelete, setPasswordToDelete] = useState<Password | null>(
        null
    );
    const [isDeleting, setIsDeleting] = useState(false);

    // Group management state
    const [isEditGroupModalOpen, setIsEditGroupModalOpen] = useState(false);
    const [editingGroup, setEditingGroup] = useState<PasswordGroup | null>(
        null
    );
    const [groupName, setGroupName] = useState("");
    const [shareUserIds, setShareUserIds] = useState<number[]>([]);
    const [sharePugIds, setSharePugIds] = useState<number[]>([]);

    const [isDeleteGroupModalOpen, setIsDeleteGroupModalOpen] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState<PasswordGroup | null>(
        null
    );
    const [isDeletingGroup, setIsDeletingGroup] = useState(false);

    const [openGroupMenuId, setOpenGroupMenuId] = useState<string | null>(null);

    const [sharedInfoModalItem, setSharedInfoModalItem] = useState<{
        title: string;
        sections: { title: string; items: string[] }[];
    } | null>(null);
    const [sharedInfoModalSearch, setSharedInfoModalSearch] = useState("");

    // Group handlers
    const handleEditGroupSubmit = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (!groupName.trim()) return;
        try {
            if (editingGroup) {
                await passwordGroupApi.update(editingGroup.id, {
                    name: groupName,
                    shared_user_ids: shareUserIds,
                    shared_password_user_group_ids: sharePugIds,
                });
                showSuccess(t("passwords.groups.updated", "Grupo actualizado"));
            } else {
                await passwordGroupApi.create({
                    name: groupName,
                    shared_user_ids: shareUserIds,
                    shared_password_user_group_ids: sharePugIds,
                });
                showSuccess(t("passwords.groups.created", "Grupo creado"));
            }
            setIsEditGroupModalOpen(false);
            loadPasswords();
        } catch (error) {
            console.error(error);
            showError(t("common.error", "Ha ocurrido un error"));
        }
    };

    const confirmDeleteGroup = async () => {
        if (!groupToDelete) return;
        setIsDeletingGroup(true);
        try {
            await passwordGroupApi.delete(groupToDelete.id);
            showSuccess(t("passwords.groups.deleted", "Grupo eliminado"));
            setIsDeleteGroupModalOpen(false);
            loadPasswords();
        } catch (error) {
            console.error(error);
            showError(t("common.error", "Ha ocurrido un error"));
        } finally {
            setIsDeletingGroup(false);
        }
    };

    const loadPasswords = async () => {
        setLoading(true);
        try {
            const [data, groupsData, usersData, pugsData] = await Promise.all([
                passwordApi.getAll(),
                passwordGroupApi.getAll(),
                authApi.users(),
                passwordUserGroupApi.getAll(),
            ]);
            setItems(data.data || data);
            setGroups(groupsData.data || groupsData);
            setUsers(usersData.data || usersData);
            setPugs(pugsData.data || pugsData);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPasswords();

        const handleClickOutside = () => setOpenMenuId(null);
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    const handleSave = async (data: PasswordFormData) => {
        try {
            if (selectedPassword) {
                await passwordApi.update(selectedPassword.id, data);
            } else {
                await passwordApi.create(data);
            }
            setIsModalOpen(false);
            loadPasswords();
            showSuccess(t("common.saved"));
        } catch (error) {
            console.error(error);
            showError(t("passwords.save_error"));
        }
    };

    const handleDelete = (row: Password) => {
        setPasswordToDelete(row);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!passwordToDelete) return;
        setIsDeleting(true);
        try {
            await passwordApi.delete(passwordToDelete.id);
            loadPasswords();
            showSuccess(t("passwords.deleted"));
            setIsDeleteModalOpen(false);
        } catch (error) {
            console.error(error);
            showError(t("passwords.delete_error"));
        } finally {
            setIsDeleting(false);
        }
    };

    /*
        key: 'owner',
        label: t('passwords.table.owner'),
        render: (row) => (
            <div className="text-xs">
                {row.owner?.name}
            </div>
        )
    */

    const handleCopy = async (
        id: number,
        text: string,
        successKey: string,
        errorKey: string
    ) => {
        setCopiedState({ id, field: "username" });
        setTimeout(() => setCopiedState(null), 2000);
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
            } else {
                const textArea = document.createElement("textarea");
                textArea.value = text;
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                document.execCommand("copy");
                textArea.remove();
            }
            showSuccess(t(successKey));
        } catch (error) {
            console.error(error);
            showError(t(errorKey));
        }
    };

    const handleCopyPassword = async (row: Password) => {
        setCopiedState({ id: row.id, field: "password" });
        setTimeout(() => setCopiedState(null), 2000);
        try {
            const pwd = await passwordApi.reveal(row.id);
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(pwd);
            } else {
                const textArea = document.createElement("textarea");
                textArea.value = pwd;
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                document.execCommand("copy");
                textArea.remove();
            }
            showSuccess(t("passwords.copied"));
        } catch (error) {
            console.error(error);
            showError(t("passwords.copy_error"));
        }
    };

    const displayGroups = useMemo(() => {
        const map = new Map<number, PasswordGroup>();
        groups.forEach((g) => map.set(g.id, g));
        items.forEach((item) => {
            if (item.group && !map.has(item.group.id)) {
                map.set(item.group.id, item.group);
            }
        });
        return Array.from(map.values()).sort((a, b) =>
            a.name.localeCompare(b.name)
        );
    }, [groups, items]);

    // Only groups owned by the current user (for table/groups tabs)
    const ownedGroups = useMemo(() => {
        return displayGroups.filter((g) => g.user_id == user?.id);
    }, [displayGroups, user]);

    const columns: TableColumn<Password>[] = [
        {
            key: "title",
            label: t("passwords.table.title"),
            sortable: true,
        },
        {
            key: "group",
            label: t("passwords.form.group_name", "Grupo"),
            render: (row) => {
                const groupName = row.group_id
                    ? displayGroups.find((g) => g.id === row.group_id)?.name
                    : t("passwords.filter.general", "General");
                return groupName || "";
            },
        },
        {
            key: "username",
            label: t("passwords.table.username"),
            sortable: true,
            render: (row) =>
                row.username ? (
                    <div className="flex items-center gap-2 group">
                        <span>{row.username}</span>
                        <button
                            onClick={() =>
                                handleCopy(
                                    row.id,
                                    row.username!,
                                    "passwords.user_copied",
                                    "passwords.user_copy_error"
                                )
                            }
                            className={`p-1 rounded transition-all ${copiedState?.id === row.id && copiedState?.field === "username" ? "text-green-500" : "text-slate-400 hover:text-primary"}`}
                            title={t("passwords.title_copy_user")}
                        >
                            <Icon
                                value={
                                    copiedState?.id === row.id &&
                                    copiedState?.field === "username"
                                        ? "check"
                                        : "copy"
                                }
                                size={16}
                            />
                        </button>
                    </div>
                ) : (
                    "-"
                ),
        },
        {
            key: "password",
            label: t("passwords.table.password"),
            render: (row) => (
                <div className="flex items-center gap-2 group">
                    <span className="">••••••••</span>
                    <button
                        onClick={() => handleCopyPassword(row)}
                        className={`p-1 rounded transition-all ${copiedState?.id === row.id && copiedState?.field === "password" ? "text-green-500" : "text-slate-400 hover:text-primary"}`}
                        title={t("passwords.title_copy_pwd")}
                    >
                        <Icon
                            value={
                                copiedState?.id === row.id &&
                                copiedState?.field === "password"
                                    ? "check"
                                    : "copy"
                            }
                            size={16}
                        />
                    </button>
                </div>
            ),
        },
        {
            key: "url",
            label: t("passwords.table.url"),
            render: (row) =>
                row.url ? (
                    <a
                        href={row.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary hover:underline flex items-center gap-1"
                    >
                        {row.url}
                    </a>
                ) : (
                    ""
                ),
        },
        {
            key: "shared_users",
            label: t("passwords.groups.share_with_users"),
            type: "multivalue",
            max: 0,
            multiValueExtractor: (row) =>
                row.user_id == user?.id
                    ? row.shared_with?.map((u: any) => u.name) || []
                    : [],
        },
        {
            key: "shared_groups",
            label: t("passwords.groups.share_with_user_groups"),
            type: "multivalue",
            max: 0,
            multiValueExtractor: (row) =>
                row.user_id == user?.id
                    ? row.shared_with_password_user_groups?.map(
                          (g: any) => g.name
                      ) || []
                    : [],
        },
    ];

    const actions: TableAction<Password>[] = [
        {
            label: t("common.edit"),
            icon: <Icon value="edit" size={16} />,
            onClick: (row) => {
                setSelectedPassword(row);
                setIsModalOpen(true);
            },
            show: (row) => row.user_id == user?.id, // Only owner can edit
        },
        {
            label: t("common.delete"),
            icon: <Icon value="delete" size={16} />,
            variant: "delete",
            onClick: handleDelete,
            show: (row) => row.user_id == user?.id, // Only owner can delete
        },
    ];

    const itemsWithSearch = useMemo(() => {
        return items
            .map((item) => {
                const groupName = item.group_id
                    ? displayGroups.find((g) => g.id === item.group_id)?.name
                    : t("passwords.filter.general", "General");
                return {
                    ...item,
                    _search_text:
                        `${item.title} ${item.username || ""} ${item.url || ""} ${groupName || ""}`.toLowerCase(),
                };
            })
            .sort((a, b) => a.title.localeCompare(b.title));
    }, [items, displayGroups, t]);

    const searchedItems = useMemo(() => {
        if (!searchQuery.trim()) return itemsWithSearch;
        const lowerQuery = searchQuery.toLowerCase();
        return itemsWithSearch.filter((item) =>
            item._search_text.includes(lowerQuery)
        );
    }, [itemsWithSearch, searchQuery]);

    // Only owned passwords (for table tab)
    const ownedSearchedItems = useMemo(() => {
        return searchedItems.filter((item) => item.user_id == user?.id);
    }, [searchedItems, user]);

    const filteredItems = useMemo(() => {
        let result = ownedSearchedItems;

        if (selectedGroupId !== "all") {
            result = result.filter((item) =>
                selectedGroupId === "null"
                    ? !item.group_id
                    : item.group_id === selectedGroupId
            );
        }

        return result;
    }, [ownedSearchedItems, selectedGroupId]);

    const groupedItems = useMemo(() => {
        const grouped: Record<string, Password[]> = {};
        searchedItems.forEach((item) => {
            const groupId = item.group_id ? String(item.group_id) : "0";
            if (!grouped[groupId]) grouped[groupId] = [];
            grouped[groupId].push(item);
        });
        return grouped;
    }, [searchedItems]);

    const filteredGroupsList = useMemo(() => {
        // List tab shows ALL groups (including shared with me)
        let result = [
            { id: "0", name: t("passwords.filter.general") },
            ...displayGroups.map((g) => ({ ...g, id: String(g.id) })),
        ];
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            result = result.filter(
                (g) =>
                    g.name.toLowerCase().includes(query) ||
                    (groupedItems[g.id] && groupedItems[g.id].length > 0)
            );
        }
        return result;
    }, [displayGroups, searchQuery, t, groupedItems]);

    const tabs: TabItem<"list" | "table" | "groups">[] = [
        {
            id: "list",
            label: t("common.list"),
            icon: <Icon value="layout" size={16} />,
        },
        {
            id: "table",
            label: t("common.table"),
            icon: <Icon value="table" size={16} />,
        },
        {
            id: "groups",
            label: t("passwords.groups.title"),
            icon: <Icon value="users" size={16} />,
        },
    ];

    return (
        <div className="page">
            <PageHeader
                compact
                title={t("passwords.title")}
                subtitle={t("passwords.subtitle")}
                action={{
                    label:
                        activeTab === "groups"
                            ? groupsSubTab === "user_groups"
                                ? t("passwords.user_groups.create")
                                : t("passwords.groups.create")
                            : t("passwords.new"),
                    icon: "plus",
                    onClick: () => {
                        if (activeTab === "groups") {
                            if (groupsSubTab === "user_groups") {
                                setTriggerCreateUserGroup((prev) => prev + 1);
                            } else {
                                setEditingGroup(null);
                                setGroupName("");
                                setShareUserIds([]);
                                setSharePugIds([]);
                                setIsEditGroupModalOpen(true);
                            }
                        } else {
                            setSelectedPassword(null);
                            setIsModalOpen(true);
                        }
                    },
                }}
            />

            <Tabs
                className="mb-4"
                variant="underline"
                tabs={tabs}
                activeTab={activeTab}
                onChange={(t) => {
                    setActiveTab(t as "list" | "table" | "groups");
                    if (t === "groups") {
                        setGroupsSubTab("password_groups");
                    }
                }}
                urlPrefix="/passwords"
                urlDefaultTab="list"
            />

            {activeTab === "list" && (
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full">
                    <SearchInput
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder={t("passwords.table.search")}
                        wrapperClassName="flex-1 mb-4"
                    />
                </div>
            )}

            {activeTab === "groups" ? (
                <PasswordGroupsView
                    groups={ownedGroups}
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                    users={users}
                    subTab={groupsSubTab}
                    onChangeSubTab={setGroupsSubTab}
                    triggerCreateUserGroup={triggerCreateUserGroup}
                    onEditGroup={(group) => {
                        setEditingGroup(group);
                        setGroupName(group.name);
                        setShareUserIds(
                            group.shared_with_users?.map((u) => u.id) || []
                        );
                        setSharePugIds(
                            group.shared_with_password_user_groups?.map(
                                (p) => p.id
                            ) || []
                        );
                        setIsEditGroupModalOpen(true);
                    }}
                    onDeleteGroup={(group) => {
                        setGroupToDelete(group);
                        setIsDeleteGroupModalOpen(true);
                    }}
                />
            ) : activeTab === "table" ? (
                <div className="w-full">
                    <Table<Password & { _search_text?: string }>
                        viewKey="vigepra_passwords"
                        viewFilters={{ group: String(selectedGroupId) }}
                        onRestoreView={(filters) =>
                            setSelectedGroupId(
                                filters.group === "null"
                                    ? "null"
                                    : Number(filters.group) || "all"
                            )
                        }
                        onClearViewFilters={() => setSelectedGroupId("all")}
                        filterFooter={
                            selectedGroupId !== "all" && (
                                <Button
                                    size="xs"
                                    variant="outline"
                                    icon="x"
                                    onClick={() => setSelectedGroupId("all")}
                                >
                                    {selectedGroupId === "null"
                                        ? t("passwords.filter.general")
                                        : ownedGroups.find(
                                              (group) =>
                                                  group.id === selectedGroupId
                                          )?.name}
                                </Button>
                            )
                        }
                        searchValue={searchQuery}
                        onSearchChange={setSearchQuery}
                        filters={
                            <Select
                                label={t("passwords.form.group")}
                                value={selectedGroupId}
                                onChange={setSelectedGroupId}
                                options={[
                                    {
                                        value: "all",
                                        label: t("passwords.filter.all"),
                                    },
                                    {
                                        value: "null",
                                        label: t("passwords.filter.general"),
                                    },
                                    ...ownedGroups.map((group) => ({
                                        value: group.id,
                                        label: group.name,
                                    })),
                                ]}
                            />
                        }
                        appearance="workspace"
                        columns={columns}
                        data={filteredItems}
                        loading={loading}
                        actions={actions}
                        actionMenu={true}
                        searchable={true}
                        defaultSortKey="title"
                        onRowClick={openDetails}
                        rowClassName={(row) =>
                            row.user_id !== user?.id
                                ? "bg-primary/5 dark:bg-primary/10 border-primary/20 dark:border-primary/30"
                                : ""
                        }
                    />
                </div>
            ) : groupIdParam ? (
                <div>
                    <div className="flex items-center justify-center mb-4">
                        <button
                            className="btn-secondary"
                            onClick={() => navigate("/passwords/list")}
                        >
                            <Icon value="arrowLeft" size={16} />
                            <span className="font-bold">
                                {groupIdParam === "0"
                                    ? t("passwords.filter.general")
                                    : displayGroups.find(
                                          (g) =>
                                              g.id.toString() === groupIdParam
                                      )?.name}
                            </span>
                        </button>
                    </div>
                    <div className="flex flex-col gap-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {groupedItems[groupIdParam]?.map((password) => (
                                <div
                                    key={password.id}
                                    onClick={() => openDetails(password)}
                                    className={`card p-4 hover:shadow-md rounded-lg transition-all cursor-pointer border flex flex-col h-full relative group
                                            ${
                                                password.user_id !== user?.id
                                                    ? "bg-primary/5 dark:bg-primary/10 border-primary/20 dark:border-primary/30"
                                                    : "bg-white dark:bg-zinc-900 border-slate-200 dark:border-zinc-800"
                                            }
                                        `}
                                >
                                    <div className="flex flex-col items-center text-center px-2 pb-2">
                                        <div className="flex flex-col flex-1 min-w-0 w-full">
                                            <h3 className="font-semibold text-slate-900 dark:text-zinc-100 truncate text-lg px-2 mb-2 mt-3">
                                                {password.title}
                                            </h3>
                                            {password.username && (
                                                <div className="relative flex items-center justify-center mt-1 w-full">
                                                    <span className="truncate text-sm text-slate-500 max-w-[80%]">
                                                        {password.username}
                                                    </span>
                                                    <div className="absolute right-0">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleCopy(
                                                                    password.id,
                                                                    password.username!,
                                                                    "passwords.user_copied",
                                                                    "passwords.user_copy_error"
                                                                );
                                                            }}
                                                            className={`px-1 pb-1 rounded transition-all flex-shrink-0 opacity-100 ${copiedState?.id === password.id && copiedState?.field === "username" ? "text-green-500" : "text-slate-400 hover:text-primary"}`}
                                                            title={t(
                                                                "passwords.title_copy_user"
                                                            )}
                                                        >
                                                            <Icon
                                                                value={
                                                                    copiedState?.id ===
                                                                        password.id &&
                                                                    copiedState?.field ===
                                                                        "username"
                                                                        ? "check"
                                                                        : "copy"
                                                                }
                                                                size={16}
                                                            />
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="mt-auto flex flex-col items-center">
                                        <div className="relative flex items-center justify-center w-full bg-slate-50 dark:bg-zinc-800/50 p-1.5 rounded-lg">
                                            <div className="font-mono text-sm text-slate-700 dark:text-zinc-300 tracking-wider">
                                                ••••••••
                                            </div>
                                            <div className="absolute right-1.5">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleCopyPassword(
                                                            password
                                                        );
                                                    }}
                                                    className={`px-1 pb-1 rounded transition-all flex-shrink-0 opacity-100 ${copiedState?.id === password.id && copiedState?.field === "password" ? "text-green-500" : "text-slate-400 hover:text-primary"}`}
                                                    title={t(
                                                        "passwords.title_copy_pwd"
                                                    )}
                                                >
                                                    <Icon
                                                        value={
                                                            copiedState?.id ===
                                                                password.id &&
                                                            copiedState?.field ===
                                                                "password"
                                                                ? "check"
                                                                : "copy"
                                                        }
                                                        size={16}
                                                    />
                                                </button>
                                            </div>
                                        </div>
                                        {password.url && (
                                            <a
                                                href={password.url}
                                                target="_blank"
                                                rel="noreferrer"
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                                className="text-primary hover:underline truncate mt-3 text-xs flex items-center justify-center gap-1.5 max-w-[90%]"
                                            >
                                                <span className="truncate">
                                                    {password.url}
                                                </span>
                                            </a>
                                        )}
                                    </div>

                                    {/* Edit / Delete / Share Menu (Left Top Corner) */}
                                    {password.user_id == user?.id && (
                                        <div className="absolute top-2 left-2">
                                            <button
                                                className="px-1.5 rounded-lg text-slate-400 hover:text-primary transition-colors"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOpenMenuId(
                                                        openMenuId ===
                                                            password.id
                                                            ? null
                                                            : password.id
                                                    );
                                                }}
                                            >
                                                <Icon
                                                    value="moreVertical"
                                                    size={16}
                                                />
                                            </button>

                                            {openMenuId === password.id && (
                                                <>
                                                    <div
                                                        className="fixed inset-0 z-40"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setOpenMenuId(null);
                                                        }}
                                                    />
                                                    <div
                                                        className="absolute left-0 top-full mt-1 w-36 bg-white dark:bg-zinc-900 rounded-xl shadow-lg border border-slate-200 dark:border-zinc-800 z-50 py-1"
                                                        onClick={(e) =>
                                                            e.stopPropagation()
                                                        }
                                                    >
                                                        <button
                                                            className="w-full text-left px-3 py-2 text-sm text-slate-700 dark:text-zinc-300 hover:bg-slate-50 dark:hover:bg-zinc-800 flex items-center gap-2"
                                                            onClick={() => {
                                                                setOpenMenuId(
                                                                    null
                                                                );
                                                                setSelectedPassword(
                                                                    password
                                                                );
                                                                setIsModalOpen(
                                                                    true
                                                                );
                                                            }}
                                                        >
                                                            <Icon
                                                                value="edit"
                                                                size={14}
                                                            />
                                                            {t("common.edit")}
                                                        </button>
                                                        <button
                                                            className="w-full text-left px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                                                            onClick={() => {
                                                                setOpenMenuId(
                                                                    null
                                                                );
                                                                setPasswordToDelete(
                                                                    password
                                                                );
                                                                setIsDeleteModalOpen(
                                                                    true
                                                                );
                                                            }}
                                                        >
                                                            <Icon
                                                                value="delete"
                                                                size={14}
                                                            />
                                                            {t("common.delete")}
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}
                                    {password.user_id == user?.id &&
                                        ((password.shared_with?.length || 0) >
                                            0 ||
                                            (password
                                                .shared_with_password_user_groups
                                                ?.length || 0) > 0) && (
                                            <div
                                                className="absolute top-2 right-2"
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                            >
                                                <Badge
                                                    variant="secondary"
                                                    icon="users"
                                                    className="cursor-pointer hover:bg-slate-200 dark:hover:bg-zinc-700 transition-colors"
                                                    onClick={(
                                                        e: React.MouseEvent
                                                    ) => {
                                                        e.stopPropagation();
                                                        setSharedInfoModalSearch(
                                                            ""
                                                        );
                                                        setSharedInfoModalItem({
                                                            title: t(
                                                                "passwords.shared_with"
                                                            ),
                                                            sections: [
                                                                ...(password.shared_with &&
                                                                password
                                                                    .shared_with
                                                                    .length > 0
                                                                    ? [
                                                                          {
                                                                              title: t(
                                                                                  "passwords.groups.share_with_users"
                                                                              ),
                                                                              items: password.shared_with.map(
                                                                                  (
                                                                                      u
                                                                                  ) =>
                                                                                      u.name
                                                                              ),
                                                                          },
                                                                      ]
                                                                    : []),
                                                                ...(password.shared_with_password_user_groups &&
                                                                password
                                                                    .shared_with_password_user_groups
                                                                    .length > 0
                                                                    ? [
                                                                          {
                                                                              title: t(
                                                                                  "passwords.groups.share_with_user_groups"
                                                                              ),
                                                                              items: password.shared_with_password_user_groups.map(
                                                                                  (
                                                                                      g
                                                                                  ) =>
                                                                                      g.name
                                                                              ),
                                                                          },
                                                                      ]
                                                                    : []),
                                                            ],
                                                        });
                                                    }}
                                                />
                                            </div>
                                        )}
                                    {password.user_id !== user?.id && (
                                        <div className="absolute top-3 right-3 text-primary/60 dark:text-primary/70">
                                            <Tooltip
                                                title={t(
                                                    "passwords.shared_with_me"
                                                )}
                                            >
                                                <span className="flex items-center">
                                                    <Icon
                                                        value="users"
                                                        size={16}
                                                    />
                                                </span>
                                            </Tooltip>
                                        </div>
                                    )}
                                </div>
                            ))}
                            {(!groupedItems[groupIdParam] ||
                                groupedItems[groupIdParam].length === 0) && (
                                <div className="col-span-full py-12 text-center text-slate-500 bg-white dark:bg-zinc-900 rounded-2xl border border-slate-200 dark:border-zinc-800">
                                    {t("passwords.table.empty")}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {filteredGroupsList.map((group) => {
                        const groupId = group.id;
                        const groupPasswords = groupedItems[groupId] || [];

                        return (
                            <div
                                key={groupId}
                                onClick={() =>
                                    navigate(`/passwords/list/${groupId}`)
                                }
                                className={`card p-6 hover:shadow-lg rounded-lg transition-all cursor-pointer border flex flex-col items-center justify-center h-full min-h-[200px] relative group
                                        ${
                                            group.id !== "0" &&
                                            group.user_id !== user?.id
                                                ? "bg-primary/5 dark:bg-primary/10 border-primary/20 dark:border-primary/30"
                                                : "bg-white dark:bg-zinc-900 border-slate-200 dark:border-zinc-800"
                                        }
                                    `}
                            >
                                {groupId !== "0" &&
                                    group.user_id == user?.id && (
                                        <div
                                            className="absolute top-3 left-3"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <button
                                                className="px-1.5 text-slate-400 hover:text-primary transition-colors"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOpenGroupMenuId(
                                                        openGroupMenuId ===
                                                            groupId
                                                            ? null
                                                            : groupId
                                                    );
                                                }}
                                            >
                                                <Icon
                                                    value="moreVertical"
                                                    size={16}
                                                />
                                            </button>

                                            {openGroupMenuId === groupId && (
                                                <>
                                                    <div
                                                        className="fixed inset-0 z-40"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setOpenGroupMenuId(
                                                                null
                                                            );
                                                        }}
                                                    ></div>
                                                    <div
                                                        className="absolute left-0 top-full mt-1 w-36 bg-white dark:bg-zinc-900 rounded-xl shadow-lg border border-slate-200 dark:border-zinc-800 z-50 py-1"
                                                        onClick={(e) =>
                                                            e.stopPropagation()
                                                        }
                                                    >
                                                        <button
                                                            className="w-full text-left px-3 py-2 text-sm text-slate-700 dark:text-zinc-300 hover:bg-slate-50 dark:hover:bg-zinc-800 flex items-center gap-2"
                                                            onClick={() => {
                                                                setOpenGroupMenuId(
                                                                    null
                                                                );
                                                                const originalGroup =
                                                                    groups.find(
                                                                        (g) =>
                                                                            String(
                                                                                g.id
                                                                            ) ===
                                                                            groupId
                                                                    );
                                                                if (
                                                                    originalGroup
                                                                ) {
                                                                    setEditingGroup(
                                                                        originalGroup
                                                                    );
                                                                    setGroupName(
                                                                        originalGroup.name
                                                                    );
                                                                    setShareUserIds(
                                                                        originalGroup.shared_with_users?.map(
                                                                            (
                                                                                u
                                                                            ) =>
                                                                                u.id
                                                                        ) || []
                                                                    );
                                                                    setSharePugIds(
                                                                        originalGroup.shared_with_password_user_groups?.map(
                                                                            (
                                                                                g
                                                                            ) =>
                                                                                g.id
                                                                        ) || []
                                                                    );
                                                                    setIsEditGroupModalOpen(
                                                                        true
                                                                    );
                                                                }
                                                            }}
                                                        >
                                                            <Icon
                                                                value="edit"
                                                                size={14}
                                                            />
                                                            {t("common.edit")}
                                                        </button>
                                                        <button
                                                            className="w-full text-left px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                                                            onClick={() => {
                                                                setOpenGroupMenuId(
                                                                    null
                                                                );
                                                                const originalGroup =
                                                                    groups.find(
                                                                        (g) =>
                                                                            String(
                                                                                g.id
                                                                            ) ===
                                                                            groupId
                                                                    );
                                                                if (
                                                                    originalGroup
                                                                ) {
                                                                    setGroupToDelete(
                                                                        originalGroup
                                                                    );
                                                                    setIsDeleteGroupModalOpen(
                                                                        true
                                                                    );
                                                                }
                                                            }}
                                                        >
                                                            <Icon
                                                                value="delete"
                                                                size={14}
                                                            />
                                                            {t("common.delete")}
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}
                                {group.id !== "0" &&
                                    group.user_id !== user?.id && (
                                        <div className="absolute top-3 right-3 text-primary/60 dark:text-primary/70">
                                            <Tooltip
                                                title={t(
                                                    "passwords.shared_with_me"
                                                )}
                                            >
                                                <span className="flex items-center">
                                                    <Icon
                                                        value="users"
                                                        size={16}
                                                    />
                                                </span>
                                            </Tooltip>
                                        </div>
                                    )}
                                <div className="w-full flex flex-col items-center justify-center">
                                    <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shrink-0">
                                        <Icon value="key-round" size={28} />
                                    </div>
                                    <h3 className="font-semibold text-slate-800 dark:text-zinc-200 text-lg text-center truncate w-full mt-4 px-2">
                                        {group.name}
                                    </h3>

                                    {group.user_id !== user?.id &&
                                        group.user && (
                                            <div className="text-xs font-medium text-primary dark:text-primary/90 bg-primary/10 dark:bg-primary/20 px-2 py-0.5 rounded flex items-center gap-1.5 mt-2">
                                                <Icon value="user" size={12} />
                                                {t(
                                                    "passwords.groups.shared_by"
                                                )}{" "}
                                                {group.user.name}
                                            </div>
                                        )}

                                    <span className="text-sm font-medium text-white bg-primary dark:bg-primary px-3 py-1 rounded-full mt-3">
                                        {groupPasswords.length}{" "}
                                        {groupPasswords.length === 1
                                            ? t(
                                                  "passwords.form.password"
                                              ).toLowerCase()
                                            : t(
                                                  "passwords.title"
                                              ).toLowerCase()}
                                    </span>
                                </div>
                                {group.user_id == user?.id &&
                                    ((group.shared_with_users?.length || 0) >
                                        0 ||
                                        (group.shared_with_password_user_groups
                                            ?.length || 0) > 0) && (
                                        <div
                                            className="absolute top-3 right-3"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <Badge
                                                title={t(
                                                    "passwords.shared_with"
                                                )}
                                                variant="secondary"
                                                icon="users"
                                                className="cursor-pointer hover:bg-slate-200 dark:hover:bg-zinc-700 transition-colors"
                                                onClick={(
                                                    e: React.MouseEvent
                                                ) => {
                                                    e.stopPropagation();
                                                    setSharedInfoModalSearch(
                                                        ""
                                                    );
                                                    setSharedInfoModalItem({
                                                        title: t(
                                                            "passwords.shared_with"
                                                        ),
                                                        sections: [
                                                            ...(group.shared_with_users &&
                                                            group
                                                                .shared_with_users
                                                                .length > 0
                                                                ? [
                                                                      {
                                                                          title: t(
                                                                              "passwords.groups.share_with_users"
                                                                          ),
                                                                          items: group.shared_with_users.map(
                                                                              (
                                                                                  u
                                                                              ) =>
                                                                                  u.name
                                                                          ),
                                                                      },
                                                                  ]
                                                                : []),
                                                            ...(group.shared_with_password_user_groups &&
                                                            group
                                                                .shared_with_password_user_groups
                                                                .length > 0
                                                                ? [
                                                                      {
                                                                          title: t(
                                                                              "passwords.groups.share_with_user_groups"
                                                                          ),
                                                                          items: group.shared_with_password_user_groups.map(
                                                                              (
                                                                                  g
                                                                              ) =>
                                                                                  g.name
                                                                          ),
                                                                      },
                                                                  ]
                                                                : []),
                                                        ],
                                                    });
                                                }}
                                            />
                                        </div>
                                    )}
                            </div>
                        );
                    })}
                </div>
            )}

            <Modal
                isOpen={isEditGroupModalOpen}
                onClose={() => setIsEditGroupModalOpen(false)}
                title={
                    editingGroup
                        ? t("passwords.groups.edit")
                        : t("passwords.groups.create")
                }
                onSubmit={handleEditGroupSubmit}
                submitDisabled={!groupName.trim()}
                maxWidth="sm"
            >
                <form
                    onSubmit={handleEditGroupSubmit}
                    className="pt-2 flex flex-col gap-4"
                >
                    <Input
                        label={t("common.name")}
                        value={groupName}
                        onChange={(e) => setGroupName(e.target.value)}
                        autoFocus
                    />
                    <div className="w-full">
                        <SearchableMultiSelect
                            label={t("passwords.groups.share_with_users")}
                            options={(users || []).map((u) => ({
                                id: u.id,
                                name: u.name,
                                subtitle: u.email,
                            }))}
                            value={shareUserIds}
                            onChange={(ids) => setShareUserIds(ids as number[])}
                            placeholder={t("passwords.form.shared_placeholder")}
                        />
                    </div>
                    <div className="w-full">
                        <SearchableMultiSelect
                            label={t("passwords.groups.share_with_user_groups")}
                            options={(pugs || []).map((p) => ({
                                id: p.id,
                                name: p.name,
                            }))}
                            value={sharePugIds}
                            onChange={(ids) => setSharePugIds(ids as number[])}
                            placeholder={t("passwords.user_groups.title")}
                        />
                    </div>
                </form>
            </Modal>

            <Modal
                isOpen={isDeleteGroupModalOpen}
                onClose={() =>
                    !isDeletingGroup && setIsDeleteGroupModalOpen(false)
                }
                title={t("common.delete")}
                submitLabel={t("common.delete")}
                submitVariant="danger"
                onSubmit={confirmDeleteGroup}
                isSubmitting={isDeletingGroup}
                maxWidth="md"
            >
                <div className="text-center text-slate-600 dark:text-zinc-300">
                    <div>
                        {t(
                            "passwords.groups.delete_confirm",
                            "¿Estás seguro de eliminar este grupo? Las contraseñas pasarán a General."
                        )}
                    </div>
                    {groupToDelete && (
                        <div className="mt-2 font-bold text-slate-900 dark:text-white">
                            {groupToDelete.name}
                        </div>
                    )}
                </div>
            </Modal>

            <Modal
                isOpen={isDetailsModalOpen}
                onClose={() => setIsDetailsModalOpen(false)}
                title={t("passwords.title")}
                footer={
                    <div className="flex items-center justify-center gap-2 w-full">
                        {selectedPasswordToView &&
                            selectedPasswordToView.user_id == user?.id && (
                                <>
                                    <button
                                        className="btn warning"
                                        onClick={() => {
                                            setIsDetailsModalOpen(false);
                                            setSelectedPassword(
                                                selectedPasswordToView
                                            );
                                            setIsModalOpen(true);
                                        }}
                                    >
                                        <Icon value="edit" size={16} />
                                        {t("common.edit")}
                                    </button>
                                    <button
                                        className="btn danger"
                                        onClick={() => {
                                            setIsDetailsModalOpen(false);
                                            setPasswordToDelete(
                                                selectedPasswordToView
                                            );
                                            setIsDeleteModalOpen(true);
                                        }}
                                    >
                                        <Icon value="delete" size={16} />
                                        {t("common.delete")}
                                    </button>
                                </>
                            )}
                    </div>
                }
            >
                {selectedPasswordToView && (
                    <div className="flex flex-col gap-4 pt-2 text-center">
                        <div className="flex flex-col items-center">
                            <span className="text-sm font-bold text-slate-700 dark:text-zinc-300">
                                {t("common.title")}
                            </span>
                            <p className="text-sm text-slate-600 dark:text-zinc-400">
                                {selectedPasswordToView.title}
                            </p>
                        </div>

                        {selectedPasswordToView.url && (
                            <div className="flex flex-col items-center">
                                <span className="text-sm font-bold text-slate-700 dark:text-zinc-300">
                                    {t("passwords.form.url")}
                                </span>
                                <p className="text-sm text-slate-600 dark:text-zinc-400">
                                    <a
                                        href={
                                            selectedPasswordToView.url.startsWith(
                                                "http"
                                            )
                                                ? selectedPasswordToView.url
                                                : `https://${selectedPasswordToView.url}`
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary hover:underline"
                                    >
                                        {selectedPasswordToView.url}
                                    </a>
                                </p>
                            </div>
                        )}

                        <div className="flex flex-col items-center">
                            <span className="text-sm font-bold text-slate-700 dark:text-zinc-300">
                                {t("passwords.form.username")}
                            </span>
                            <p className="text-sm text-slate-600 dark:text-zinc-400">
                                {selectedPasswordToView.username}
                            </p>
                        </div>

                        {selectedPasswordToView.description && (
                            <div className="flex flex-col items-center">
                                <span className="text-sm font-bold text-slate-700 dark:text-zinc-300">
                                    {t("common.description")}
                                </span>
                                <p className="text-sm text-slate-600 dark:text-zinc-400 max-w-sm whitespace-pre-wrap">
                                    {selectedPasswordToView.description}
                                </p>
                            </div>
                        )}

                        <div className="flex flex-col items-center">
                            <span className="text-sm font-bold text-slate-700 dark:text-zinc-300">
                                {t("passwords.groups.updated_at")}
                            </span>
                            <p className="text-sm text-slate-600 dark:text-zinc-400">
                                {selectedPasswordToView.updated_at
                                    ? formatDate(
                                          selectedPasswordToView.updated_at,
                                          false
                                      )
                                    : "-"}
                            </p>
                        </div>

                        {(selectedPasswordToView.shared_with?.length || 0) >
                            0 && (
                            <div className="flex flex-col items-center">
                                <span className="text-sm font-bold text-slate-700 dark:text-zinc-300">
                                    {t("passwords.groups.share_with_users")}
                                </span>
                                <div className="flex flex-wrap justify-center gap-2 mt-2">
                                    {selectedPasswordToView.shared_with?.map(
                                        (u) => (
                                            <Badge key={u.id} variant="primary">
                                                {u.name}
                                            </Badge>
                                        )
                                    )}
                                </div>
                            </div>
                        )}

                        {(selectedPasswordToView
                            .shared_with_password_user_groups?.length || 0) >
                            0 && (
                            <div className="flex flex-col items-center">
                                <span className="text-sm font-bold text-slate-700 dark:text-zinc-300">
                                    {t(
                                        "passwords.groups.share_with_user_groups"
                                    )}
                                </span>
                                <div className="flex flex-wrap justify-center gap-2 mt-2">
                                    {selectedPasswordToView.shared_with_password_user_groups?.map(
                                        (g) => (
                                            <Badge key={g.id} variant="primary">
                                                {g.name}
                                            </Badge>
                                        )
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            {isModalOpen && (
                <PasswordModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSave={handleSave}
                    password={selectedPassword}
                />
            )}

            <Modal
                isOpen={isDeleteModalOpen}
                onClose={() => !isDeleting && setIsDeleteModalOpen(false)}
                title={t("common.delete")}
                submitLabel={t("common.delete")}
                submitVariant="danger"
                onSubmit={confirmDelete}
                isSubmitting={isDeleting}
                maxWidth="md"
            >
                <div className="text-center text-slate-600 dark:text-zinc-300">
                    <div>{t("passwords.delete_confirm")}</div>
                    {passwordToDelete && (
                        <div className="mt-2 font-bold text-slate-900 dark:text-white">
                            {passwordToDelete.title}
                        </div>
                    )}
                </div>
            </Modal>

            <Modal
                isOpen={!!sharedInfoModalItem}
                onClose={() => setSharedInfoModalItem(null)}
                title={sharedInfoModalItem?.title || ""}
                hideCancel
                submitLabel={t("common.close")}
                submitVariant="secondary"
                onSubmit={() => setSharedInfoModalItem(null)}
                maxWidth="md"
            >
                {sharedInfoModalItem && (
                    <div className="flex flex-col gap-4 pt-2 pb-2">
                        <SearchInput
                            value={sharedInfoModalSearch}
                            onChange={setSharedInfoModalSearch}
                            placeholder={t("common.search")}
                        />
                        <div className="flex flex-col gap-5">
                            {sharedInfoModalItem.sections.map((section, si) => {
                                const filtered = section.items.filter((it) =>
                                    String(it)
                                        .toLowerCase()
                                        .includes(
                                            sharedInfoModalSearch.toLowerCase()
                                        )
                                );
                                return (
                                    <div key={si}>
                                        <p className="text-center text-xs font-semibold text-slate-500 dark:text-zinc-400 uppercase tracking-wider mb-2">
                                            {section.title}
                                        </p>
                                        <div className="flex flex-wrap gap-2 justify-center">
                                            {filtered.length === 0 ? (
                                                <div className="text-sm text-slate-500">
                                                    {t("common.no_results")}
                                                </div>
                                            ) : (
                                                filtered.map((it, i) => (
                                                    <Badge
                                                        key={i}
                                                        variant="primary"
                                                    >
                                                        {it}
                                                    </Badge>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
